import { Container, Sprite } from "pixi.js"
import { MapEntry } from "game-common/room/base_map_builder"

/** What the ghosts need of the map: its art, and hiding the real tiles they stand in for. */
export interface ReshapeMap {
    addTileAt: (x: number, y: number, tile: string, layerId: string, owned?: boolean) => { sprite?: Sprite }
    getTileSurface: () => Container
    canVeilBuildCell?: (x: number, y: number, layerId: string) => boolean
    setBuildVeil?: (keys: Iterable<string>) => void
}

interface Reshape {
    x: number
    y: number
    layerId: string
    tile: string
}

interface Claim {
    cells: Map<string, Reshape>
    at: number
    /** Set once its piece is built: shown solid until the map draws the new shapes, or until then. */
    settleUntil?: number
}

/** How long a built piece's reshaped neighbours wait for the map's own tiles before giving up. */
export const RESHAPE_SETTLE_MS = 2000

/**
 * The existing tiles a build preview reshapes -- the wall it joins, a roof edge turning interior, a
 * floor's trim -- shown in their planned shape. While a shown preview claims a tile, the map hides the
 * real one and a ghost of its new art stands in; once none does, the ghost goes and the real tile is
 * drawn again, in the same frame.
 *
 * Previews claim by owner: the cursor, or one planned cell. Two planned cells beside one wall both
 * reshape it, and the latest claim's art is the one shown.
 */
export class BuildReshapeGhosts {
    private claims = new Map<string, Claim>()
    private ghosts = new Map<string, { sprite: Sprite; tile: string }>()
    private clock = 0

    constructor(
        private map: () => ReshapeMap,
        /** The tile the map holds at a cell, in the same ids as a preview delta's tilePos. */
        private drawnTile: (x: number, y: number, layerId: string) => string | undefined,
        private now: () => number = () => Date.now(),
    ) {}

    /** What `owner`'s preview would change, from its server deltas. Replaces the owner's last claim. */
    claim = (owner: string, deltas: MapEntry[]) => {
        const cells = new Map<string, Reshape>()
        deltas.forEach(delta => {
            // The previewed piece itself is drawn by its own ghost; this is only what it does to its neighbours.
            if (["blocking", "skyTrigger"].includes(delta.layerId) || !delta.context?.highZ || delta.context?.previewTarget !== false) return
            const tile = String(delta.tileSpec.tilePos)
            const drawn = this.drawnTile(delta.x, delta.y, delta.layerId)
            // Only a tile already standing there is reshaped, and only if it would really change.
            if (!drawn || drawn === "0" || drawn === tile) return
            cells.set(`${delta.x}-${delta.y}-${delta.layerId}`, { x: delta.x, y: delta.y, layerId: delta.layerId, tile })
        })
        if (cells.size) this.claims.set(owner, { cells, at: ++this.clock })
        else this.claims.delete(owner)
    }

    /** The preview is gone. A settling claim outlives its owner. */
    release = (owner: string) => {
        if (this.claims.get(owner)?.settleUntil === undefined) this.claims.delete(owner)
    }

    /** The owner's piece was built: hold its reshaped neighbours solid until the map's tiles catch up. */
    settle = (owner: string) => {
        const claim = this.claims.get(owner)
        if (claim) claim.settleUntil = this.now() + RESHAPE_SETTLE_MS
    }

    /**
     * Once a frame: show the ghosts of the shown owners' claims, each at its owner's alpha, hide the
     * real tiles under them, and give every other tile back to the map.
     */
    sync = (shown: Map<string, number>) => {
        if (!this.claims.size && !this.ghosts.size) return
        const map = this.map()
        const now = this.now()
        const wanted = new Map<string, { reshape: Reshape; alpha: number; at: number }>()
        this.claims.forEach((claim, owner) => {
            if (claim.settleUntil !== undefined) {
                // The map drew the built shape where it already matches; this claim is done there.
                claim.cells.forEach((reshape, key) => {
                    if (this.drawnTile(reshape.x, reshape.y, reshape.layerId) === reshape.tile) claim.cells.delete(key)
                })
                if (!claim.cells.size || now >= claim.settleUntil) {
                    this.claims.delete(owner)
                    return
                }
            }
            const alpha = claim.settleUntil !== undefined ? 1 : shown.get(owner)
            if (alpha === undefined) return
            claim.cells.forEach((reshape, key) => {
                const current = wanted.get(key)
                if (current && current.at > claim.at) {
                    current.alpha = Math.max(current.alpha, alpha)
                    return
                }
                if (!map.canVeilBuildCell?.(reshape.x, reshape.y, reshape.layerId)) return
                wanted.set(key, { reshape, alpha: Math.max(alpha, current?.alpha ?? 0), at: claim.at })
            })
        })
        this.ghosts.forEach((ghost, key) => {
            const want = wanted.get(key)
            if (want && want.reshape.tile === ghost.tile && !ghost.sprite.destroyed) return
            ghost.sprite.parent?.removeChild(ghost.sprite)
            if (!ghost.sprite.destroyed) ghost.sprite.destroy()
            this.ghosts.delete(key)
        })
        const surface = map.getTileSurface()
        wanted.forEach(({ reshape, alpha }, key) => {
            let ghost = this.ghosts.get(key)
            if (!ghost) {
                const sprite = map.addTileAt(reshape.x, reshape.y, reshape.tile, reshape.layerId, true)?.sprite
                if (!sprite) return
                // Above the scene, as a planned piece's own ghost is.
                sprite.zIndex = 100000
                ghost = { sprite, tile: reshape.tile }
                this.ghosts.set(key, ghost)
            }
            ghost.sprite.alpha = alpha
            if (ghost.sprite.parent !== surface) surface.addChild(ghost.sprite)
        })
        map.setBuildVeil?.(this.ghosts.keys())
    }

    /** Drop every claim and hand every tile back to the map. */
    clear = () => {
        this.claims.clear()
        this.sync(new Map())
    }
}
